import { AnimationGroup, Scene, Skeleton, AnimationPropertiesOverride, AbstractMesh } from '@babylonjs/core';
import { AnimationPhase, TransitionConfig, StatePhase, PhaseTiming } from '../config/characters';
import { SubmeshInstance } from '../assets/CharacterLoader';
import { EventBus } from '../core/EventBus';
import { logger } from '../core/Logger';

export type AnimationState = AnimationPhase | string; // Allow string for custom states like attacks
export type BlendPreset = 'INSTANT' | 'LINEAR' | 'EASE_IN_OUT' | 'EASE_IN_CUBIC';

export interface TrackInfo {
  name: string;
  weight: number;
  time: number; // Normalized time 0-1
  progress: number; // Same as time, for consistency with UI
  speed: number;
  isPlaying: boolean;
  phases?: PhaseTiming; // Phase timing for this animation
}

export interface AnimationDebugState {
  active: TrackInfo | null;
  fading: TrackInfo | null;
  action: TrackInfo | null;
  isBlending: boolean;
  blendProgress: number; // 0-1
  missingAnimations: string[];
}

// Events emitted by AnimationController
export interface AnimationControllerEvents {
  'animation:phase-change': { state: AnimationState; from: StatePhase | null; to: StatePhase };
  'animation:state-change': { from: AnimationState; to: AnimationState };
  'animation:blend-complete': { state: AnimationState };
}

interface AnimationRange {
  from: number;
  to: number;
  group: AnimationGroup;
}

/**
 * Configuration for action layer animation blending
 */
export interface ActionBlendConfig {
  blendIn?: number;  // Fade-in duration in seconds (default 0.1)
  blendOut?: number; // Fade-out duration in seconds (default 0.15)
  disableRootMotion?: boolean; // If true, disable root motion while action layer is active
}

/** Default blend durations for action layer */
const DEFAULT_BLEND_IN = 0.1;
const DEFAULT_BLEND_OUT = 0.15;

/**
 * Default phase timing for animations that don't have explicit phases defined.
 * Simple 4-phase split: 15% windup, 35% active, 35% release, 15% recovery
 */
const DEFAULT_PHASE_TIMING: PhaseTiming = {
  windup: { start: 0.0, end: 0.15 },
  active: { start: 0.15, end: 0.5 },
  release: { start: 0.5, end: 0.85 },
  recovery: { start: 0.85, end: 1.0 }
};

export class AnimationController {
  private static instanceCounter = 0;
  public readonly instanceId: number;
  private animations: Map<AnimationState, AnimationRange> = new Map();
  private currentState: AnimationState = 'idle';
  private skeleton: Skeleton;
  private transitions: TransitionConfig[] = [];
  private submeshes: SubmeshInstance[] = [];

  // Blending state
  private activeGroup: AnimationGroup | null = null;
  private fadingGroup: AnimationGroup | null = null;
  private actionGroup: AnimationGroup | null = null; // Secondary layer for masked actions
  private currentMaskBones: string[] | null = null; // Bones currently masked by action layer
  private currentMaskBoneSet: Set<string> | null = null; // Fast lookup + prefix variants
  private actionState: AnimationState | null = null;
  private isBlending: boolean = false;
  private blendDuration: number = 0.2;
  private blendTimer: number = 0;
  private rootMesh: AbstractMesh | null = null;

  // Action layer blend state
  private actionBlendState: 'idle' | 'fading-in' | 'playing' | 'fading-out' = 'idle';
  private actionBlendTimer: number = 0;
  private actionBlendInDuration: number = DEFAULT_BLEND_IN;
  private actionBlendOutDuration: number = DEFAULT_BLEND_OUT;
  private actionCurrentWeight: number = 0; // Current blend weight for action layer (0-1)
  private actionDisableRootMotion: boolean = false; // If true, action layer overrides root motion to disabled
  private actionOnComplete: (() => void) | null = null;

  // One-shot state
  private oneShotCallback: (() => void) | null = null;
  private oneShotExitTime: number | null = null; // Frame number to exit at

  // Missing animation tracking
  private missingAnimations: Set<string> = new Set();
  
  // Phase tracking (new - for animation state system)
  private currentPhase: StatePhase | null = null;
  private stateElapsedTime: number = 0;
  private stateNormalizedTime: number = 0;
  private eventBus: EventBus | null = null;
  private characterId: string = '';  // For event identification
  
  // Debug controls
  private debugTimeScale: number = 1.0;
  private debugPaused: boolean = false;
  
  // Loop range state (for channeled animations with intro/loop/outro)
  private loopRangeState: {
    state: AnimationState;
    range: AnimationRange;
    loopStart: number;  // Frame number
    loopEnd: number;    // Frame number
    phase: 'intro' | 'looping' | 'outro';
    introPlayed: boolean;
    onOutroComplete?: () => void;
  } | null = null;

  /**
   * Create animation controller using manual blending for AnimationGroups
   * @param scene - The Babylon scene
   * @param skeleton - The character skeleton
   * @param animationGroups - Either a Map<AnimationState, AnimationGroup> or AnimationGroup[]
   * @param transitions - Optional list of transition rules
   * @param statePrefix - Prefix to extract state names (only used for array input)
   * @param rootMesh - Optional root mesh for applying scaling/mirroring
   * @param submeshes - Optional list of submeshes to sync animations with
   * @param eventBus - Optional EventBus for emitting animation events
   * @param characterId - Optional character ID for event identification
   */
  constructor(
    _scene: Scene,
    skeleton: Skeleton,
    animationGroups: Map<AnimationState, AnimationGroup> | AnimationGroup[], 
    transitions: TransitionConfig[] = [],
    statePrefix: string = 'Player',
    rootMesh?: AbstractMesh,
    submeshes: SubmeshInstance[] = [],
    eventBus?: EventBus,
    characterId?: string
  ) {
    this.instanceId = ++AnimationController.instanceCounter;
    this.skeleton = skeleton;
    this.transitions = transitions;
    this.rootMesh = rootMesh || null;
    this.submeshes = submeshes;
    this.eventBus = eventBus || null;
    this.characterId = characterId || '';

    // Setup skeleton animation properties (kept for reference, though we use manual blending)
    this.skeleton.animationPropertiesOverride = new AnimationPropertiesOverride();
    this.skeleton.animationPropertiesOverride.enableBlending = true;
    this.skeleton.animationPropertiesOverride.blendingSpeed = 0.05;
    this.skeleton.animationPropertiesOverride.loopMode = 1;

    // If passed a Map, use it directly
    if (animationGroups instanceof Map) {
      for (const [state, group] of animationGroups.entries()) {
        this.animations.set(state, {
          from: group.from,
          to: group.to,
          group: group
        });
        group.stop();
      }
    } else {
      // Legacy: Map animation groups to states based on naming convention
      for (const group of animationGroups) {
        const stateName = this.extractStateName(group.name, statePrefix);
        if (stateName) {
          this.animations.set(stateName, {
            from: group.from,
            to: group.to,
            group: group
          });
          group.stop();
        }
      }
    }

    logger.animation.debug(`AnimationController initialized with ${this.animations.size} states`);

    // Start with idle animation if available, otherwise run
    const idleRange = this.animations.get('idle');
    const runRange = this.animations.get('run');
    const startRange = idleRange || runRange;

    if (startRange) {
      this.currentState = idleRange ? 'idle' : 'run';
      this.activeGroup = startRange.group;
      
      // Start immediately with full weight
      const defaultSpeed = this.activeGroup.metadata?.defaultSpeed || 1.0;
      this.activeGroup.speedRatio = defaultSpeed;
      this.activeGroup.start(true, this.activeGroup.speedRatio, startRange.from, startRange.to);
      this.activeGroup.setWeightForAllAnimatables(1.0);
      
      logger.animation.debug(`Started ${this.currentState} animation`);
    } else {
      logger.animation.warn('No idle or run animation found!');
    }
  }

  private extractStateName(name: string, _prefix: string): AnimationState | null {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('idle')) return 'idle';
    if (lowerName.includes('run')) return 'run';
    if (lowerName.includes('walk')) return 'walk';
    if (lowerName.includes('jump')) return 'jump';
    if (lowerName.includes('fall')) return 'fall';
    if (lowerName.includes('land')) return 'land';
    if (lowerName.includes('turn')) return 'turn';
    return null;
  }

  setState(newState: AnimationState, duration: number = 0.2, _preset: BlendPreset = 'LINEAR'): void {
    if (newState === this.currentState) {
      return;
    }

    // If we're in a loop range state (channeled animation), clean it up properly
    // This ensures callbacks are invoked even if the animation is interrupted
    if (this.loopRangeState) {
      const callback = this.loopRangeState.onOutroComplete;
      logger.animation.debug(`setState: Clearing loopRangeState (was in phase '${this.loopRangeState.phase}'), transitioning to '${newState}'`);
      this.loopRangeState = null;
      // Call the callback so CombatSystem can finalize the attack properly
      if (callback) {
        logger.animation.debug(`setState: Invoking loopRange outro callback before transition`);
        callback();
      }
    }

    const nextRange = this.animations.get(newState);
    if (!nextRange) {
      // Make fall/land animations optional - if not found, just skip it
      if (newState === 'fall' || newState === 'land') {
        logger.animation.debug(`${newState} animation not configured, skipping`);
        return;
      }
      
      const available = Array.from(this.animations.keys()).join(', ');
      throw new Error(
        `❌ Animation state '${newState}' not found\n` +
        `   Available states: ${available || '(none)'}`
      );
    }
    
    // Determine blend duration based on transitions
    let blendDuration = duration;
    
    // 1. Check for specific transition rule (Highest Priority)
    // We check: Specific From -> Specific To
    const specificRule = this.transitions.find(t => t.from === this.currentState && t.to === newState);
    
    // 2. Check for wildcard From -> Specific To
    const fromWildcard = this.transitions.find(t => t.from === '*' && t.to === newState);
    
    // 3. Check for Specific From -> Wildcard To
    const toWildcard = this.transitions.find(t => t.from === this.currentState && t.to === '*');

    if (specificRule) {
        blendDuration = specificRule.duration ?? duration;
    } else if (fromWildcard) {
        blendDuration = fromWildcard.duration ?? duration;
    } else if (toWildcard) {
        blendDuration = toWildcard.duration ?? duration;
    }

    logger.animation.debug(`[${this.characterId || 'unknown'}] setState: ${this.currentState} -> ${newState}`);

    // Save old state for event emission
    const oldState = this.currentState;
    
    // Reset phase tracking for new state
    this.resetPhaseTracking();

    // Handle instant switch
    if (blendDuration <= 0) {
        if (this.activeGroup) {
            this.activeGroup.stop();
            this.activeGroup.setWeightForAllAnimatables(0);
        }
        if (this.fadingGroup) {
            this.fadingGroup.stop();
            this.fadingGroup.setWeightForAllAnimatables(0);
            this.fadingGroup = null;
        }
        
        this.activeGroup = nextRange.group;
        this.currentState = newState;
        
        // Emit state change event
        if (this.eventBus) {
            this.eventBus.emit('animation:state-change', {
                characterId: this.characterId,
                from: oldState,
                to: newState
            });
        }
        
        // Use default speed from metadata
        const defaultSpeed = this.activeGroup.metadata?.defaultSpeed || 1.0;
        this.activeGroup.speedRatio = defaultSpeed;
        
        this.activeGroup.start(true, this.activeGroup.speedRatio, nextRange.from, nextRange.to);
        this.activeGroup.setWeightForAllAnimatables(1.0);
        this.isBlending = false;
        return;
    }

    // If we are already blending, the current active group becomes the fading group
    if (this.fadingGroup) {
        // If there was already a fading group, stop it completely
        this.fadingGroup.stop();
        this.fadingGroup.setWeightForAllAnimatables(0);
    }

    // Current active becomes fading
    this.fadingGroup = this.activeGroup;
    
    // New becomes active
    this.activeGroup = nextRange.group;
    this.currentState = newState;
    
    // Emit state change event
    if (this.eventBus) {
        this.eventBus.emit('animation:state-change', {
            characterId: this.characterId,
            from: oldState,
            to: newState
        });
    }
    
    // Use default speed from metadata
    const defaultSpeed = this.activeGroup.metadata?.defaultSpeed || 1.0;
    this.activeGroup.speedRatio = defaultSpeed;
    
    // Apply mirroring if specified in metadata
    if (this.rootMesh) {
        // Default to no mirroring (false) if not specified
        const shouldMirror = this.activeGroup.metadata?.mirror || false;
        
        // Apply to X scaling (assuming X is the side axis for the mesh)
        // We use Math.abs to ensure we're setting the absolute sign, not flipping relative to current
        this.rootMesh.scaling.x = shouldMirror ? -Math.abs(this.rootMesh.scaling.x) : Math.abs(this.rootMesh.scaling.x);
    }
    
    // Start the new animation with 0 weight
    this.activeGroup.start(true, this.activeGroup.speedRatio, nextRange.from, nextRange.to);
    this.activeGroup.setWeightForAllAnimatables(0);
    
    // Setup blending
    this.isBlending = true;
    this.blendDuration = blendDuration;
    
    // Override blend duration if specified in metadata (Medium Priority - overridden by transitions above if we wanted, but let's keep this as fallback or specific override?)
    // Actually, transitions should probably win.
    // But let's say metadata is "default blend into me" which is effectively (* -> To).
    // So if we didn't find a specific rule, we can check metadata.
    
    if (!specificRule && !fromWildcard && !toWildcard && this.activeGroup.metadata?.blendConfig?.duration) {
        this.blendDuration = this.activeGroup.metadata.blendConfig.duration;
    }
    
    this.blendTimer = 0;
    
    // If no previous group (e.g. first start), just set to 1
    if (!this.fadingGroup) {
        this.activeGroup.setWeightForAllAnimatables(1.0);
        this.isBlending = false;
    }

    // Sync submeshes
    this.syncSubmeshes(newState, true);
  }

  /**
   * Sync submesh animations with the main character state
   */
  private syncSubmeshes(state: string, loop: boolean): void {
      for (const sub of this.submeshes) {
          const animName = sub.animationMap?.[state];
          let played = false;
          
          if (animName) {
              const group = sub.animations.get(animName);
              if (group) {
                  // Stop others
                  for (const g of sub.animations.values()) {
                      if (g !== group) g.stop();
                  }
                  
                  // Sync speed
                  if (this.activeGroup) {
                      group.speedRatio = this.activeGroup.speedRatio;
                  }
                  
                  // Play
                  group.start(loop, group.speedRatio, group.from, group.to);
                  played = true;
              } else {
                  // Animation mapped but not found - warn once
                  logger.animation.warn(`Submesh '${sub.id}' animation '${animName}' for state '${state}' not found`);
              }
          }
          
          if (!played) {
              // No animation for this state, stop all (return to default/bind pose)
              for (const g of sub.animations.values()) {
                  g.stop();
              }
          }
      }
  }

  /**
   * Set the speed ratio for the current animation
   */
  setSpeedRatio(speed: number): void {
    if (this.activeGroup) {
      this.activeGroup.speedRatio = speed;
    }
    if (this.fadingGroup) {
      this.fadingGroup.speedRatio = speed;
    }
  }

  /**
   * Update blending weights and phase tracking
   */
  update(deltaTime: number): void {
    // Apply debug controls
    if (this.debugPaused) {
      return; // Skip all updates when paused
    }
    deltaTime *= this.debugTimeScale;
    
    // Update phase tracking
    this.updatePhaseTracking(deltaTime);
    
    // Update loop range state (for channeled animations)
    this.updateLoopRangeState();
    
    // Check for one-shot early exit
    const checkGroup = this.actionGroup || this.activeGroup;
    
    if (this.oneShotCallback && checkGroup && this.oneShotExitTime !== null) {
        // Get current frame
        // AnimationGroup doesn't expose currentFrame directly easily for all animatables, 
        // but we can check the first animatable's masterFrame
        const currentFrame = checkGroup.animatables[0]?.masterFrame;
        
        if (currentFrame !== undefined && currentFrame >= this.oneShotExitTime) {
            // Trigger callback early
            const callback = this.oneShotCallback;
            this.oneShotCallback = null;
            this.oneShotExitTime = null;
            
            // Remove the standard end callback to avoid double calling
            checkGroup.onAnimationGroupEndObservable.clear();
            
            logger.animation.debug(`One-shot early exit triggered at frame ${currentFrame.toFixed(1)}`);
            callback();
        }
    }

    if (this.isBlending && this.activeGroup && this.fadingGroup) {
        this.blendTimer += deltaTime;
        
        // Calculate blend factor (0 to 1)
        const t = Math.min(this.blendTimer / this.blendDuration, 1.0);
        
        // Apply weights (Linear blend)
        // You could implement other easing functions here based on _preset
        this.activeGroup.setWeightForAllAnimatables(t);
        this.fadingGroup.setWeightForAllAnimatables(1.0 - t);
        
        // Finish blending
        if (t >= 1.0) {
            this.isBlending = false;
            this.fadingGroup.stop();
            this.fadingGroup.setWeightForAllAnimatables(0);
            this.fadingGroup = null;
            this.activeGroup.setWeightForAllAnimatables(1.0);
            
            // Emit blend complete event
            if (this.eventBus) {
                this.eventBus.emit('animation:blend-complete', {
                    characterId: this.characterId,
                    state: this.currentState
                });
            }
        }
    }

    // Update action layer blend (fade in/out)
    this.updateActionLayerBlend(deltaTime);

    // Re-apply mask to base layers during action layer animation
    // This ensures smooth weight transitions for masked bones
    if (this.currentMaskBones && this.actionBlendState !== 'idle') {
        this.applyMaskToBaseLayers();
    }
  }

  getCurrentState(): AnimationState {
    return this.currentState;
  }

  /**
   * Get the current phase of the animation (windup, active, release, recovery)
   */
  getCurrentPhase(): StatePhase | null {
    return this.currentPhase;
  }

  /**
   * Get the normalized time (0-1) through the current animation
   */
  getNormalizedTime(): number {
    return this.stateNormalizedTime;
  }

  /**
   * Normalized time (0-1) for the current action-layer animation, if any.
   * Used by systems (e.g. CombatSystem) that need timeline events for masked actions.
   */
  getActionNormalizedTime(): number | null {
    if (!this.actionGroup || !this.actionState) return null;

    const range = this.animations.get(this.actionState);
    if (!range) return null;

    // Find an animatable with non-zero weight (masked bones have weight, non-masked are zeroed)
    let animatable = this.actionGroup.animatables.find(a => a.weight > 0);
    // Fallback to first animatable if all have zero weight (shouldn't happen during play)
    if (!animatable) animatable = this.actionGroup.animatables[0];
    if (!animatable) return null;

    const totalFrames = range.to - range.from;
    if (totalFrames <= 0) return null;

    const currentFrame = animatable.masterFrame;
    const rawFrame = currentFrame - range.from;
    const isLooping = this.actionGroup.loopAnimation ?? false;
    const frameInAnim = isLooping
      ? ((rawFrame % totalFrames) + totalFrames) % totalFrames // safe modulo for looping
      : Math.min(Math.max(rawFrame, 0), totalFrames); // clamp for non-looping
    return Math.max(0, Math.min(1, frameInAnim / totalFrames));
  }

  /**
   * Get the elapsed time in seconds since entering the current state
   */
  getStateElapsedTime(): number {
    return this.stateElapsedTime;
  }

  /**
   * Get the phase timing configuration for the current animation
   */
  getCurrentPhaseTiming(): PhaseTiming {
    if (!this.activeGroup) return DEFAULT_PHASE_TIMING;
    
    const phases = this.activeGroup.metadata?.phases as PhaseTiming | undefined;
    return phases || DEFAULT_PHASE_TIMING;
  }

  /**
   * Check if the current animation can exit (based on exit time configuration)
   */
  canExitCurrentState(): boolean {
    if (!this.activeGroup) return true;
    
    const exitTimeConfig = this.activeGroup.metadata?.exitTime as { enabled: boolean; time: number } | undefined;
    
    // If no exit time configured, can always exit
    if (!exitTimeConfig || !exitTimeConfig.enabled) {
      return true;
    }
    
    // Check if we've passed the exit time
    return this.stateNormalizedTime >= exitTimeConfig.time;
  }

  /**
   * Get the exit time for the current animation (or null if not configured)
   */
  getExitTime(): number | null {
    if (!this.activeGroup) return null;
    
    const exitTimeConfig = this.activeGroup.metadata?.exitTime as { enabled: boolean; time: number } | undefined;
    
    if (!exitTimeConfig || !exitTimeConfig.enabled) {
      return null;
    }
    
    return exitTimeConfig.time;
  }

  /**
   * Calculate the current phase based on normalized time
   */
  private calculatePhase(normalizedTime: number, timing: PhaseTiming): StatePhase | null {
    // Check each phase in order
    if (timing.windup && normalizedTime >= timing.windup.start && normalizedTime < timing.windup.end) {
      return 'windup';
    }
    if (timing.active && normalizedTime >= timing.active.start && normalizedTime < timing.active.end) {
      return 'active';
    }
    if (timing.release && normalizedTime >= timing.release.start && normalizedTime < timing.release.end) {
      return 'release';
    }
    if (timing.recovery && normalizedTime >= timing.recovery.start && normalizedTime <= timing.recovery.end) {
      return 'recovery';
    }
    
    // Fallback: if time is past all phases, we're in recovery
    if (normalizedTime >= 1.0) {
      return 'recovery';
    }
    
    return null;
  }

  /**
   * Update phase tracking based on current animation state
   */
  private updatePhaseTracking(deltaTime: number): void {
    if (!this.activeGroup) return;
    
    // Update elapsed time
    this.stateElapsedTime += deltaTime;
    
    // Calculate normalized time from the active group
    const animatable = this.activeGroup.animatables[0];
    if (!animatable) return;
    
    const currentFrame = animatable.masterFrame;
    const range = this.animations.get(this.currentState);
    if (!range) return;
    
    const totalFrames = range.to - range.from;
    if (totalFrames <= 0) return;
    
    // Calculate normalized time (0-1)
    // For looping animations, this wraps around
    const rawFrame = currentFrame - range.from;
    const isLooping = this.activeGroup?.loopAnimation ?? false;
    const frameInAnim = isLooping
      ? ((rawFrame % totalFrames) + totalFrames) % totalFrames // safe modulo
      : Math.min(Math.max(rawFrame, 0), totalFrames);
    this.stateNormalizedTime = Math.max(0, Math.min(1, frameInAnim / totalFrames));
    
    // Get phase timing
    const timing = this.getCurrentPhaseTiming();
    
    // Calculate current phase
    const newPhase = this.calculatePhase(this.stateNormalizedTime, timing);
    
    // Emit event if phase changed
    if (newPhase !== this.currentPhase) {
      const oldPhase = this.currentPhase;
      this.currentPhase = newPhase;
      
      if (this.eventBus && newPhase) {
        this.eventBus.emit('animation:phase-change', {
          characterId: this.characterId,
          state: this.currentState,
          from: oldPhase,
          to: newPhase
        });
      }
    }
  }

  /**
   * Reset phase tracking when entering a new state
   */
  private resetPhaseTracking(): void {
    this.stateElapsedTime = 0;
    this.stateNormalizedTime = 0;
    this.currentPhase = null;
  }

  getAvailableStates(): AnimationState[] {
    return Array.from(this.animations.keys());
  }

  /**
   * Check if the current animation locks facing (prevents logical rotation updates)
   */
  isFacingLocked(): boolean {
    return !!this.activeGroup?.metadata?.lockFacing;
  }

  /**
   * Check if the current animation applies root motion.
   * Returns false if action layer explicitly disables root motion.
   */
  shouldApplyRootMotion(): boolean {
    // If action layer is active and has root motion disabled, return false
    if (this.actionGroup && this.actionDisableRootMotion) {
      return false;
    }
    return !!this.activeGroup?.metadata?.applyRootMotion;
  }

  stopAll(): void {
    // If we're in a loop range state, clean it up properly
    if (this.loopRangeState) {
      const callback = this.loopRangeState.onOutroComplete;
      logger.animation.debug(`stopAll: Clearing loopRangeState (was in phase '${this.loopRangeState.phase}')`);
      this.loopRangeState = null;
      if (callback) callback();
    }

    for (const range of this.animations.values()) {
      range.group.stop();
    }
    this.activeGroup = null;
    this.fadingGroup = null;
    this.isBlending = false;
  }

  /**
   * Pause the current animation at its current frame
   */
  pauseCurrent(): void {
    if (this.activeGroup) {
      this.activeGroup.pause();
    }
    if (this.fadingGroup) {
        this.fadingGroup.pause();
    }
  }

  /**
   * Resume the current animation
   */
  resumeCurrent(): void {
    if (this.activeGroup) {
        // AnimationGroup.play() resumes from pause
        this.activeGroup.play(true); 
    }
    if (this.fadingGroup) {
        this.fadingGroup.play(true);
    }
  }

  /**
   * Get the duration of the currently active animation in seconds
   */
  getActiveDuration(): number {
      const group = this.actionGroup || this.activeGroup;
      if (!group) return 0;
      
      const frameCount = group.to - group.from;
      const fps = 60; 
      const speed = group.speedRatio || 1.0;
      
      return Math.abs(frameCount / (fps * speed));
  }
  getDebugState(): AnimationDebugState {
    return {
      active: this.getTrackInfo(this.activeGroup),
      fading: this.getTrackInfo(this.fadingGroup),
      action: this.getTrackInfo(this.actionGroup),
      isBlending: this.isBlending,
      blendProgress: this.isBlending ? Math.min(this.blendTimer / this.blendDuration, 1.0) : 0,
      missingAnimations: Array.from(this.missingAnimations)
    };
  }

  private getTrackInfo(group: AnimationGroup | null): TrackInfo | null {
    if (!group) return null;
    
    // Calculate normalized time
    let normalizedTime = 0;
    if (group.animatables.length > 0) {
      const animatable = group.animatables[0];
      const currentFrame = animatable.masterFrame;
      const from = group.from;
      const to = group.to;
      const duration = to - from;
      if (duration > 0) {
        normalizedTime = (currentFrame - from) / duration;
      }
    }

    // Get weight (check first animatable)
    let weight = 0;
    if (group.animatables.length > 0) {
      weight = group.animatables[0].weight;
    }

    // Get phase timing for this animation (from group metadata)
    const phases = this.getPhaseTimingForGroup(group);

    return {
      name: group.name,
      weight: weight,
      time: normalizedTime,
      progress: normalizedTime,
      speed: group.speedRatio,
      isPlaying: group.isPlaying,
      phases: phases
    };
  }

  /**
   * Get phase timing for a given animation group
   */
  private getPhaseTimingForGroup(group: AnimationGroup): PhaseTiming | undefined {
    if (!group || !group.metadata) return undefined;
    return group.metadata.phases as PhaseTiming | undefined;
  }

  getDebugInfo(): string {
      const lines: string[] = [];
      lines.push(`State: ${this.currentState}`);
      
      // Phase and timing info
      if (this.currentPhase) {
          lines.push(`Phase: ${this.currentPhase} (t: ${(this.stateNormalizedTime * 100).toFixed(1)}%)`);
      }
      
      // Exit time info
      const exitTime = this.getExitTime();
      if (exitTime !== null) {
          const canExit = this.canExitCurrentState();
          lines.push(`Exit: ${canExit ? '✓' : '⏳'} (at ${(exitTime * 100).toFixed(0)}%)`);
      }
      
      if (this.activeGroup) {
          const weight = this.activeGroup.animatables[0]?.weight;
          lines.push(`Active: ${this.activeGroup.name} (w: ${weight !== undefined ? weight.toFixed(2) : 'N/A'})`);
      }
      
      if (this.fadingGroup) {
          const weight = this.fadingGroup.animatables[0]?.weight;
          lines.push(`Fading: ${this.fadingGroup.name} (w: ${weight !== undefined ? weight.toFixed(2) : 'N/A'})`);
      }
      
      if (this.actionGroup) {
          const weight = this.actionGroup.animatables[0]?.weight;
          lines.push(`Action: ${this.actionGroup.name} (w: ${weight !== undefined ? weight.toFixed(2) : 'N/A'})`);
          if (this.currentMaskBones) {
              lines.push(`Mask: ${this.currentMaskBones.length} bones`);
          }
      }
      
      if (this.missingAnimations.size > 0) {
          lines.push(`⚠️ Missing: ${Array.from(this.missingAnimations).join(', ')}`);
      }
      
      return lines.join('\n');
  }

  /**
   * Check if an animation state exists
   */
  hasAnimation(state: AnimationState): boolean {
      return this.animations.has(state);
  }

  /**
   * Get list of missing animations that were requested
   */
  getMissingAnimations(): string[] {
      return Array.from(this.missingAnimations);
  }

  /**
   * Get metadata for a specific animation state
   */
  getAnimationMetadata(state: AnimationState): any {
      const range = this.animations.get(state);
      return range?.group.metadata;
  }

  /**
   * Play an animation in loop mode (supports masked action layer)
   */
  playLooping(state: AnimationState, speedRatio: number = 1.0): void {
    const range = this.animations.get(state);
    if (!range) {
      if (!this.missingAnimations.has(state)) {
        this.missingAnimations.add(state);
        logger.animation.debug(`Animation '${state}' not found - skipping loop`);
      }
      return;
    }

    // If we're in a loop range state (channeled animation), clean it up properly
    if (this.loopRangeState) {
      const callback = this.loopRangeState.onOutroComplete;
      logger.animation.debug(`playLooping: Clearing loopRangeState (was in phase '${this.loopRangeState.phase}')`);
      this.loopRangeState = null;
      if (callback) callback();
    }

    const maskBones = range.group.metadata?.maskBones as string[] | undefined;
    if (maskBones && maskBones.length > 0) {
      // Action-layer looping
      const previousGroup = this.actionGroup;
      if (previousGroup) {
        previousGroup.stop();
        previousGroup.setWeightForAllAnimatables(0);
        this.restoreBaseWeights();
      }

      const nextGroup = range.group;
      this.actionGroup = nextGroup;
      this.actionState = state;
      this.currentMaskBones = maskBones;

      const defaultSpeed = nextGroup.metadata?.defaultSpeed || 1.0;
      nextGroup.speedRatio = defaultSpeed * speedRatio;

      const startOffset = Number(nextGroup.metadata?.startOffset);
      const totalFrames = range.to - range.from;
      const startFrame = Number.isFinite(startOffset) && totalFrames > 0
        ? Math.max(range.from, Math.min(range.to, range.from + startOffset * totalFrames))
        : range.from;

      nextGroup.start(true, nextGroup.speedRatio, startFrame, range.to);
      nextGroup.setWeightForAllAnimatables(1.0);

      this.applyMaskToBaseLayers();
      this.syncSubmeshes(state, true);
      return;
    }

    // Base-layer looping
    if (this.fadingGroup) {
      this.fadingGroup.stop();
      this.fadingGroup.setWeightForAllAnimatables(0);
      this.fadingGroup = null;
    }
    if (this.activeGroup) {
      this.activeGroup.stop();
      this.activeGroup.setWeightForAllAnimatables(0);
    }

    this.activeGroup = range.group;
    this.currentState = state;
    this.isBlending = false;

    const defaultSpeed = this.activeGroup.metadata?.defaultSpeed || 1.0;
    this.activeGroup.speedRatio = defaultSpeed * speedRatio;

    const startOffset = Number(this.activeGroup.metadata?.startOffset);
    const totalFrames = range.to - range.from;
    const startFrame = Number.isFinite(startOffset) && totalFrames > 0
      ? Math.max(range.from, Math.min(range.to, range.from + startOffset * totalFrames))
      : range.from;

    this.activeGroup.start(true, this.activeGroup.speedRatio, startFrame, range.to);
    this.activeGroup.setWeightForAllAnimatables(1.0);

    this.syncSubmeshes(state, true);
  }

  /**
   * Stop a looping animation (action layer or base layer)
   */
  stopLooping(): void {
    if (this.actionGroup) {
      this.actionGroup.stop();
      this.actionGroup.setWeightForAllAnimatables(0);
      this.actionGroup = null;
      this.actionState = null;
      this.currentMaskBones = null;
      this.currentMaskBoneSet = null;
      this.restoreBaseWeights();
    }

    if (this.activeGroup) {
      this.activeGroup.stop();
      this.activeGroup.setWeightForAllAnimatables(0);
    }
    
    // Also clear loop range state
    this.loopRangeState = null;
  }

  /**
   * Play an animation with intro → loop → outro phases.
   * Uses loopRange metadata to define the looping section.
   * 
   * 1. Plays from start to loopRange.end (intro + first loop iteration)
   * 2. Loops between loopRange.start and loopRange.end (sustain)
   * 3. On releaseLoopRange(), plays from current position to end (outro)
   * 
   * @param state Animation state to play
   * @param speedRatio Speed multiplier
   */
  playLoopingWithRange(state: AnimationState, speedRatio: number = 1.0): void {
    const range = this.animations.get(state);
    if (!range) {
      if (!this.missingAnimations.has(state)) {
        this.missingAnimations.add(state);
        logger.animation.debug(`Animation '${state}' not found - skipping loop with range`);
      }
      return;
    }

    const loopRangeMeta = range.group.metadata?.loopRange as { start: number; end: number } | undefined;
    
    // If no loop range defined, fall back to regular looping
    if (!loopRangeMeta) {
      logger.animation.debug(`Animation '${state}' has no loopRange - using standard loop`);
      this.playLooping(state, speedRatio);
      return;
    }

    // Calculate frame numbers from normalized values
    const totalFrames = range.to - range.from;
    const loopStart = range.from + loopRangeMeta.start * totalFrames;
    const loopEnd = range.from + loopRangeMeta.end * totalFrames;

    logger.animation.debug(
      `playLoopingWithRange: ${state} (frames ${range.from}-${range.to}, ` +
      `intro ends at ${loopStart.toFixed(1)}, loop ${loopStart.toFixed(1)}-${loopEnd.toFixed(1)}, outro from ${loopEnd.toFixed(1)})`
    );

    // Stop any existing animation
    if (this.fadingGroup) {
      this.fadingGroup.stop();
      this.fadingGroup.setWeightForAllAnimatables(0);
      this.fadingGroup = null;
    }
    if (this.activeGroup) {
      this.activeGroup.stop();
      this.activeGroup.setWeightForAllAnimatables(0);
    }

    // Set up the loop range state
    this.loopRangeState = {
      state,
      range,
      loopStart,
      loopEnd,
      phase: 'intro',
      introPlayed: false,
    };

    this.activeGroup = range.group;
    this.currentState = state;
    this.isBlending = false;

    const defaultSpeed = this.activeGroup.metadata?.defaultSpeed || 1.0;
    this.activeGroup.speedRatio = defaultSpeed * speedRatio;

    // Calculate start frame with startOffset support
    const startOffset = Number(this.activeGroup.metadata?.startOffset);
    const startFrame = Number.isFinite(startOffset) && totalFrames > 0
      ? Math.max(range.from, Math.min(loopStart, range.from + startOffset * totalFrames))
      : range.from;

    // Start playing from startOffset (or beginning) to loopStart (intro phase)
    // We'll switch to looping once we reach loopStart
    this.activeGroup.start(false, this.activeGroup.speedRatio, startFrame, loopStart);
    this.activeGroup.setWeightForAllAnimatables(1.0);

    this.syncSubmeshes(state, true);
  }

  /**
   * Release a loop-range animation to play the outro.
   * Called when the channel button is released.
   * 
   * @param onComplete Callback when outro animation completes
   */
  releaseLoopRange(onComplete?: () => void): void {
    if (!this.loopRangeState || !this.activeGroup) {
      logger.animation.debug('releaseLoopRange: No active loop range animation');
      if (onComplete) onComplete();
      return;
    }

    const { range, loopEnd, phase } = this.loopRangeState;
    
    // Already in outro, just update callback
    if (phase === 'outro') {
      this.loopRangeState.onOutroComplete = onComplete;
      return;
    }

    logger.animation.debug(`releaseLoopRange: Transitioning to outro phase`);

    // Get current frame position
    const currentFrame = this.activeGroup.animatables[0]?.masterFrame ?? loopEnd;
    
    // Transition to outro: play from current position to end
    this.loopRangeState.phase = 'outro';
    this.loopRangeState.onOutroComplete = onComplete;
    
    // Stop current playback and restart from current position to end
    this.activeGroup.stop();
    this.activeGroup.start(false, this.activeGroup.speedRatio, currentFrame, range.to);
    this.activeGroup.setWeightForAllAnimatables(1.0);
  }

  /**
   * Check if currently in a loop-range animation
   */
  isInLoopRange(): boolean {
    return this.loopRangeState !== null;
  }

  /**
   * Get current loop-range phase
   */
  getLoopRangePhase(): 'intro' | 'looping' | 'outro' | null {
    return this.loopRangeState?.phase ?? null;
  }

  /**
   * Update loop range state (called from update loop)
   */
  private updateLoopRangeState(): void {
    if (!this.loopRangeState || !this.activeGroup) return;

    const { loopStart, loopEnd, phase, range, state } = this.loopRangeState;
    const animatable = this.activeGroup.animatables[0];
    
    // If no animatable, the animation stopped - handle based on phase
    if (!animatable) {
      if (phase === 'intro') {
        // Intro finished - start looping
        logger.animation.debug(`Loop range: Intro complete (animation ended), starting loop section`);
        this.loopRangeState.phase = 'looping';
        this.loopRangeState.introPlayed = true;
        
        // Start looping between loopStart and loopEnd
        this.activeGroup.start(true, this.activeGroup.speedRatio, loopStart, loopEnd);
        this.activeGroup.setWeightForAllAnimatables(1.0);
      } else if (phase === 'outro') {
        // Outro finished
        logger.animation.debug(`Loop range: Outro complete (animation ended)`);
        const callback = this.loopRangeState.onOutroComplete;
        this.loopRangeState = null;
        if (callback) callback();
      } else if (phase === 'looping') {
        // Looping animation stopped unexpectedly - restart it
        logger.animation.warn(`Loop range: Looping animation stopped unexpectedly for '${state}', restarting`);
        this.activeGroup.start(true, this.activeGroup.speedRatio, loopStart, loopEnd);
        this.activeGroup.setWeightForAllAnimatables(1.0);
      }
      return;
    }

    const currentFrame = animatable.masterFrame;

    if (phase === 'intro') {
      // Check if we've reached the end of the intro (loopStart)
      if (currentFrame >= loopStart - 0.5) {
        logger.animation.debug(`Loop range: Intro complete at frame ${currentFrame.toFixed(1)}, starting loop section ${loopStart.toFixed(1)}-${loopEnd.toFixed(1)}`);
        this.loopRangeState.phase = 'looping';
        this.loopRangeState.introPlayed = true;
        
        // Start looping between loopStart and loopEnd
        this.activeGroup.stop();
        this.activeGroup.start(true, this.activeGroup.speedRatio, loopStart, loopEnd);
        this.activeGroup.setWeightForAllAnimatables(1.0);
      }
    } else if (phase === 'outro') {
      // Check if outro is complete
      if (currentFrame >= range.to - 0.5 || !this.activeGroup.isPlaying) {
        logger.animation.debug(`Loop range: Outro complete at frame ${currentFrame.toFixed(1)}`);
        const callback = this.loopRangeState.onOutroComplete;
        this.loopRangeState = null;
        if (callback) callback();
      }
    }
    // 'looping' phase continues until releaseLoopRange is called
  }

  /**
   * Play an animation once and then execute callback
   * @param state Animation state to play
   * @param onComplete Callback when animation finishes
   * @param speedRatio Speed multiplier (default 1.0)
   * @param maskBones Optional bone mask to override animation metadata (for action-layer attacks)
   * @param blendConfig Optional blend in/out durations for action layer
   */
  playOneShot(state: AnimationState, onComplete?: () => void, speedRatio: number = 1.0, maskBones?: string[], blendConfig?: ActionBlendConfig): void {
    const range = this.animations.get(state);
    if (!range) {
      // Make fall/land animations optional
      // Track missing animation and skip gracefully
      if (!this.missingAnimations.has(state)) {
        this.missingAnimations.add(state);
        logger.animation.debug(`Animation '${state}' not found - skipping`);
      }
      if (onComplete) onComplete();
      return;
    }

    // If we're in a loop range state (channeled animation), clean it up properly
    if (this.loopRangeState) {
      const callback = this.loopRangeState.onOutroComplete;
      logger.animation.debug(`playOneShot: Clearing loopRangeState (was in phase '${this.loopRangeState.phase}')`);
      this.loopRangeState = null;
      if (callback) callback();
    }

    logger.animation.debug(`playOneShot: ${state} (speed: ${speedRatio})`);

    // Check if this is a masked animation (Action Layer)
    // Allow override via parameter, otherwise check animation metadata
    const effectiveMaskBones = maskBones ?? (range.group.metadata?.maskBones as string[] | undefined);
    
    if (effectiveMaskBones && effectiveMaskBones.length > 0) {
        this.playMaskedOneShot(state, range, effectiveMaskBones, onComplete, speedRatio, blendConfig);
        return;
    }

    // Standard One-Shot (Replaces Active)
    // Stop current blending
    if (this.fadingGroup) {
        this.fadingGroup.stop();
        this.fadingGroup.setWeightForAllAnimatables(0);
        this.fadingGroup = null;
    }
    
    // Stop active group
    if (this.activeGroup) {
        this.activeGroup.stop();
        this.activeGroup.setWeightForAllAnimatables(0);
    }

    // Set new active group
    this.activeGroup = range.group;
    this.currentState = state;
    this.isBlending = false;

    // Play once (loop=false)
    // Ensure we play at least one frame
    if (range.to <= range.from) {
        logger.animation.warn(`Animation '${state}' has invalid range [${range.from}, ${range.to}]`);
    }
    
    // Use default speed from metadata if available, otherwise 1.0
    const defaultSpeed = this.activeGroup.metadata?.defaultSpeed || 1.0;
    this.activeGroup.speedRatio = defaultSpeed * speedRatio;

    const startOffset = Number(this.activeGroup.metadata?.startOffset);
    const totalFrames = range.to - range.from;
    const startFrame = Number.isFinite(startOffset) && totalFrames > 0
      ? Math.max(range.from, Math.min(range.to, range.from + startOffset * totalFrames))
      : range.from;

    this.activeGroup.start(false, this.activeGroup.speedRatio, startFrame, range.to);
    this.activeGroup.setWeightForAllAnimatables(1.0);

    // DEBUG: Check if animation targets are valid
    if (this.activeGroup.targetedAnimations.length > 0) {
      const firstTarget = this.activeGroup.targetedAnimations[0];
      logger.animation.debug(`[AnimCtrl] First animation target: ${firstTarget.target?.name}, isDisposed=${firstTarget.target?.['isDisposed']?.() ?? 'N/A'}`);
    }

    // Sync submeshes (one-shot)
    this.syncSubmeshes(state, false);

    this.setupOneShotCompletion(state, range, onComplete);
  }

    private playMaskedOneShot(state: AnimationState, range: AnimationRange, maskBones: string[], onComplete?: () => void, speedRatio: number = 1.0, blendConfig?: ActionBlendConfig) {
      logger.animation.log(`🎭 Playing MASKED animation '${state}' on ${maskBones.length} bones (blendIn: ${blendConfig?.blendIn ?? DEFAULT_BLEND_IN}s, blendOut: ${blendConfig?.blendOut ?? DEFAULT_BLEND_OUT}s)`);

      const previousGroup = this.actionGroup;
      if (previousGroup) {
        previousGroup.stop();
        previousGroup.setWeightForAllAnimatables(0);
        this.restoreBaseWeights();
      }

      const nextGroup = range.group;
      this.actionGroup = nextGroup;
      this.actionState = state;
      this.currentMaskBones = maskBones;
      this.currentMaskBoneSet = null;
      
      // Store blend durations from config or use defaults
      this.actionBlendInDuration = blendConfig?.blendIn ?? DEFAULT_BLEND_IN;
      this.actionBlendOutDuration = blendConfig?.blendOut ?? DEFAULT_BLEND_OUT;
      this.actionDisableRootMotion = blendConfig?.disableRootMotion ?? false;

      const defaultSpeed = nextGroup.metadata?.defaultSpeed || 1.0;
      nextGroup.speedRatio = defaultSpeed * speedRatio;

      const startOffset = Number(nextGroup.metadata?.startOffset);
      const totalFrames = range.to - range.from;
      const startFrame = Number.isFinite(startOffset) && totalFrames > 0
        ? Math.max(range.from, Math.min(range.to, range.from + startOffset * totalFrames))
        : range.from;

      // Start the animation with full weight initially (inverse mask will zero non-masked bones)
      nextGroup.start(false, nextGroup.speedRatio, startFrame, range.to);
      nextGroup.setWeightForAllAnimatables(1.0);
      
      // Skip fade-in to avoid timing issues with attack events - go directly to full weight
      this.actionCurrentWeight = 1.0;
      this.actionBlendState = 'playing';
      this.actionBlendTimer = 0;
      this.actionOnComplete = onComplete ?? null;

      // Build the mask set first (done inside applyMaskToBaseLayers)
      // Then apply masks to both layers
      this.applyMaskToBaseLayers(); // This builds currentMaskBoneSet
      this.applyInverseMaskToActionLayer(1.0); // Now zero non-masked bones

      // Set up animation end to trigger fade-out instead of immediate completion
      const triggerFadeOut = () => {
        if (this.actionGroup !== nextGroup) {
          return;
        }
        // Don't complete immediately - start fading out
        if (this.actionBlendState !== 'fading-out') {
          this.actionBlendState = 'fading-out';
          this.actionBlendTimer = 0;
          logger.animation.debug(`🎭 Action animation ended, starting fade-out`);
        }
      };

      this.setupOneShotCompletion(state, range, triggerFadeOut, true, nextGroup);
    }

  private applyMaskToBaseLayers() {
      if (!this.currentMaskBones) return;

      // Build/refresh lookup set if needed.
      // Some assets target TransformNodes named like 'Spine' while masks use 'mixamorig:Spine' (or vice-versa).
      if (!this.currentMaskBoneSet) {
        const set = new Set<string>();
        for (const name of this.currentMaskBones) {
          set.add(name);
          if (name.includes(':')) {
            set.add(name.split(':').pop() as string);
          } else {
            set.add(`mixamorig:${name}`);
          }
        }
        this.currentMaskBoneSet = set;
        logger.animation.log(`🎭 Built mask set with ${set.size} entries: ${[...set].slice(0, 5).join(', ')}...`);
      }

      // Use tracked action weight for smooth blending of base layer
      const actionWeight = this.actionCurrentWeight;
      const baseLayerMaskWeight = 1.0 - actionWeight; // Inverse of action weight

      // Reduce masked bone weights on base layer (so action layer takes over upper body)
      let baseZeroCount = 0;
      const applyToGroup = (group: AnimationGroup | null) => {
          if (!group) return;
          for (const animatable of group.animatables) {
              const targetName = animatable.target?.name ?? '<no-name>';
              // Strip _instance_XXXXX suffix for matching
              const baseName = targetName.replace(/_instance_\d+$/, '');
              const shortName = baseName.split(':').pop() ?? '';
              const isInMask = this.currentMaskBoneSet!.has(baseName) ||
                               this.currentMaskBoneSet!.has(shortName) ||
                               this.currentMaskBoneSet!.has(`mixamorig:${shortName}`);
              if (isInMask) {
                  // Set to inverse of action weight for smooth blend
                  animatable.weight = baseLayerMaskWeight;
                  baseZeroCount++;
              }
          }
      };

      applyToGroup(this.activeGroup);
      applyToGroup(this.fadingGroup);
      
      logger.animation.debug(`🎭 Base layer: set ${baseZeroCount} masked bone weights to ${baseLayerMaskWeight.toFixed(2)}`);
  }

  /**
   * Apply inverse mask to action layer - zero weight on bones NOT in the mask.
   * This ensures legs continue from base layer while upper body plays the action.
   */
  private applyInverseMaskToActionLayer(actionWeight: number = 1.0) {
      if (!this.actionGroup || !this.currentMaskBoneSet) return;
      
      for (const animatable of this.actionGroup.animatables) {
          const targetName = animatable.target?.name ?? '<no-name>';
          // Strip _instance_XXXXX suffix for matching
          const baseName = targetName.replace(/_instance_\d+$/, '');
          const shortName = baseName.split(':').pop() ?? '';
          const isInMask = this.currentMaskBoneSet.has(baseName) ||
                           this.currentMaskBoneSet.has(shortName) ||
                           this.currentMaskBoneSet.has(`mixamorig:${shortName}`);
          
          // Bones IN mask get full action weight, bones NOT in mask get 0
          animatable.weight = isInMask ? actionWeight : 0;
      }
  }

  private restoreBaseWeights() {
      if (this.activeGroup) {
          // Restore all weights to 1.0
          // Note: If we were blending, this might snap. 
          // Ideally we should restore to 'current blend weight' but for now 1.0 is safe as we usually attack from full state.
          this.activeGroup.setWeightForAllAnimatables(1.0);
      }
  }

  /**
   * Update action layer blend state (fade in/out)
   */
  private updateActionLayerBlend(deltaTime: number) {
      if (this.actionBlendState === 'idle' || !this.actionGroup) return;

      this.actionBlendTimer += deltaTime;

      if (this.actionBlendState === 'fading-in') {
          // Fade in: weight goes 0 -> 1 on masked bones only
          const t = Math.min(this.actionBlendTimer / this.actionBlendInDuration, 1.0);
          this.actionCurrentWeight = t;
          // Apply inverse mask with current blend weight
          this.applyInverseMaskToActionLayer(t);
          
          if (t >= 1.0) {
              this.actionBlendState = 'playing';
              this.actionCurrentWeight = 1.0;
              logger.animation.debug(`🎭 Action fade-in complete`);
          }
      } else if (this.actionBlendState === 'playing') {
          // Ensure full weight on masked bones during play
          this.actionCurrentWeight = 1.0;
          this.applyInverseMaskToActionLayer(1.0);
          
          // Check if we're near the end of the animation - start early fade-out
          const normalizedTime = this.getActionNormalizedTime();
          if (normalizedTime !== null) {
              // Calculate when to start fading out so we finish fading when animation ends
              const speedRatio = this.actionGroup.speedRatio || 1.0;
              const range = this.actionState ? this.animations.get(this.actionState) : null;
              const totalFrames = range ? (range.to - range.from) : 0;
              // Use standard 30fps assumption for Babylon.js animations
              const animDurationSec = totalFrames > 0 ? (totalFrames / 30) / speedRatio : 1.0;
              
              // Calculate normalized time when we should start fading
              const fadeStartNormalized = 1.0 - (this.actionBlendOutDuration / animDurationSec);
              
              if (normalizedTime >= fadeStartNormalized && fadeStartNormalized > 0) {
                  this.actionBlendState = 'fading-out';
                  this.actionBlendTimer = 0;
                  logger.animation.debug(`🎭 Action near end (${(normalizedTime * 100).toFixed(0)}%), starting fade-out`);
              }
          }
      } else if (this.actionBlendState === 'fading-out') {
          // Fade out: weight goes 1 -> 0 on masked bones
          const t = Math.min(this.actionBlendTimer / this.actionBlendOutDuration, 1.0);
          const actionWeight = 1.0 - t;
          this.actionCurrentWeight = actionWeight;
          // Apply inverse mask with fading weight
          this.applyInverseMaskToActionLayer(actionWeight);
          
          if (t >= 1.0) {
              // Fully faded out - clean up
              this.actionGroup.stop();
              this.actionGroup.setWeightForAllAnimatables(0);
              
              const callback = this.actionOnComplete;
              
              this.actionGroup = null;
              this.actionState = null;
              this.currentMaskBones = null;
              this.currentMaskBoneSet = null;
              this.actionBlendState = 'idle';
              this.actionCurrentWeight = 0;
              this.actionDisableRootMotion = false;
              this.actionOnComplete = null;
              
              this.restoreBaseWeights();
              
              logger.animation.debug(`🎭 Action fade-out complete`);
              
              if (callback) callback();
          }
      }
  }

    private setupOneShotCompletion(
    state: AnimationState,
    range: AnimationRange,
    onComplete?: () => void,
    isActionLayer: boolean = false,
    groupOverride?: AnimationGroup
    ) {
    const group = groupOverride ?? (isActionLayer ? this.actionGroup : this.activeGroup);
    if (!group) return;

    let earlyExitScheduled = false;

    if (!isActionLayer) {
      const rule = this.transitions.find(t => t.from === state && (t.blendRatioFrom !== undefined || t.blendRatio !== undefined));

      if (rule && onComplete) {
        const ratio = rule.blendRatioFrom ?? rule.blendRatio ?? 0;
        if (ratio > 0) {
          const durationFrames = range.to - range.from;
          this.oneShotExitTime = range.to - (ratio * durationFrames);
          this.oneShotCallback = onComplete;
          earlyExitScheduled = true;
          logger.animation.debug(`Scheduled early exit for '${state}' at frame ${this.oneShotExitTime.toFixed(1)}`);
        }
      }
    }

    // Handle completion (fallback if no early exit or if early exit fails)
    const onEnd = () => {
      if (isActionLayer) {
        if (this.actionGroup !== group) {
          group.onAnimationGroupEndObservable.removeCallback(onEnd);
          return;
        }
      } else if (this.activeGroup !== group) {
        group.onAnimationGroupEndObservable.removeCallback(onEnd);
        return;
      }

        // If early exit was scheduled, we only run if it hasn't run yet (checked by oneShotCallback existence/match)
        // If NO early exit was scheduled, we ALWAYS run.
        
        if (earlyExitScheduled) {
            if (this.oneShotCallback === onComplete) {
                this.oneShotCallback = null;
                this.oneShotExitTime = null;
                if (onComplete) onComplete();
            }
        } else {
            // No early exit was planned, so this is the only exit point
            if (onComplete) onComplete();
        }
        
        group.onAnimationGroupEndObservable.removeCallback(onEnd);
    };
    group.onAnimationGroupEndObservable.add(onEnd);
  }

  isActionPlaying(): boolean {
    return !!this.actionGroup;
  }
  
  /**
   * Set debug time scale (1.0 = normal, 0.5 = half speed, etc.)
   */
  setDebugTimeScale(scale: number): void {
    this.debugTimeScale = Math.max(0.1, Math.min(2.0, scale));
  }
  
  /**
   * Set debug pause state
   */
  setDebugPaused(paused: boolean): void {
    this.debugPaused = paused;
  }
  
  /**
   * Get debug pause state
   */
  isDebugPaused(): boolean {
    return this.debugPaused;
  }
  
  /**
   * Get debug time scale
   */
  getDebugTimeScale(): number {
    return this.debugTimeScale;
  }
}
